/**
 * Powerplant: shaft power from the `.DAT` power rating (offset 32), the
 * altitude lapse, propeller thrust, fuel burn, and the carburettor-ice cut.
 *
 * The lapse law is AD-007c's Open Question #3: the game stores each aircraft's
 * service ceiling exactly (offset 108 ÷ 5 = feet) but no lapse curve at all. We
 * model it as `P(h) = P0 · σ(h)^n` and solve `n` per aircraft against the
 * stored ceiling (aircraft-spec.ts), so the ceilings emerge from the physics
 * rather than being clamped — which is what AD-007c asks for, with the flag
 * kept so a later RE pass can turn it off.
 */

import { CLEAR_CARBURETTOR, updateCarburettor, type CarburettorState } from "./carburetor.ts";
import type { RngBank } from "./rng.ts";
import type { Tunables } from "./tunables.ts";
import { clamp } from "./units.ts";

export interface PropulsionSpec {
  /** Raw `.DAT` power rating, offset 32 (43 Morane … 82 D.VII). */
  readonly powerRatingRaw: number;
  readonly shaftPowerW: number;
  readonly propellerEfficiency: number;
  /** Exponent n in `P(h) = P0 · σ(h)^n`, solved against the stored service ceiling. */
  readonly powerLapseExponent: number;
  /** Below this speed, `ηP/V` is capped to a constant static thrust. */
  readonly staticThrustSpeedMps: number;
}

export interface FuelSpec {
  readonly capacityKg: number;
  readonly kgPerJoule: number;
}

export interface EngineState {
  /** Smoothed throttle setting actually reaching the engine, 0..1. */
  readonly throttle: number;
  readonly deliveredPowerW: number;
  readonly carbIced: boolean;
  readonly carbThawSeconds: number;
  readonly fuelKg: number;
  readonly fuelExhausted: boolean;
}

export interface EngineUpdate {
  readonly propulsion: PropulsionSpec;
  readonly fuel: FuelSpec;
  readonly tunables: Tunables;
  readonly throttleInput: number;
  readonly altitudeFt: number;
  /** σ to lapse against — callers pass 1 when the altitude-lapse flag is off. */
  readonly densityRatio: number;
  /** 0..1 engine component health; scales delivered power. */
  readonly engineHealth: number;
  readonly limitedFuel: boolean;
  readonly carbIceEnabled: boolean;
  readonly rng: RngBank;
  readonly dt: number;
}

/** Shaft power available at a density ratio, after the altitude lapse. */
export function availableShaftPowerW(propulsion: PropulsionSpec, densityRatio: number): number {
  const sigma = Math.max(densityRatio, 1e-4);
  return propulsion.shaftPowerW * Math.pow(sigma, propulsion.powerLapseExponent);
}

/**
 * Propeller thrust. `ηP/V` diverges at zero speed, so below
 * `staticThrustSpeedMps` the thrust is held at its value there — the standard
 * cheap stand-in for a real propeller map.
 */
export function thrustN(propulsion: PropulsionSpec, deliveredPowerW: number, airspeedMps: number): number {
  const effectiveSpeed = Math.max(airspeedMps, propulsion.staticThrustSpeedMps);
  return (propulsion.propellerEfficiency * deliveredPowerW) / effectiveSpeed;
}

export function fuelBurnKgPerSecond(fuel: FuelSpec, deliveredPowerW: number): number {
  return fuel.kgPerJoule * Math.max(0, deliveredPowerW);
}

function carburettorOf(engine: EngineState): CarburettorState {
  return { iced: engine.carbIced, thawSeconds: engine.carbThawSeconds };
}

export function updateEngine(engine: EngineState, update: EngineUpdate): EngineState {
  const lag = Math.max(update.tunables.throttleLagSeconds, 1e-3);
  const blend = clamp(update.dt / lag, 0, 1);
  const throttle = engine.throttle + (clamp(update.throttleInput, 0, 1) - engine.throttle) * blend;

  const carb = update.carbIceEnabled
    ? updateCarburettor(carburettorOf(engine), {
        enabled: true,
        altitudeFt: update.altitudeFt,
        dt: update.dt,
        tunables: update.tunables,
        rng: update.rng,
      })
    : CLEAR_CARBURETTOR;

  const fuelExhausted = update.limitedFuel && engine.fuelKg <= 0;
  const cut = carb.iced || fuelExhausted;
  const deliveredPowerW = cut
    ? 0
    : availableShaftPowerW(update.propulsion, update.densityRatio) *
      throttle *
      clamp(update.engineHealth, 0, 1);

  const burn = update.limitedFuel ? fuelBurnKgPerSecond(update.fuel, deliveredPowerW) * update.dt : 0;
  const fuelKg = Math.max(0, engine.fuelKg - burn);

  return {
    throttle,
    deliveredPowerW,
    carbIced: carb.iced,
    carbThawSeconds: carb.thawSeconds,
    fuelKg,
    fuelExhausted: update.limitedFuel && fuelKg <= 0,
  };
}
